import { createClientId } from './createClientId';

export const USER_PROFILE_STORAGE_KEY = 'flower-blend:user-profile:v1';
export const USER_PROFILE_VERSION = 1;
export const MAX_DISPLAY_NAME_LENGTH = 80;

export interface UserProfile {
  id: string;
  displayName: string;
  profileVersion: typeof USER_PROFILE_VERSION;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class UserProfileError extends Error {}

export function normalizeDisplayName(value: string): string {
  const displayName = value.trim();
  if (!displayName) throw new UserProfileError('Please enter your name.');
  if (displayName.length > MAX_DISPLAY_NAME_LENGTH) {
    throw new UserProfileError(
      `Name must be ${MAX_DISPLAY_NAME_LENGTH} characters or fewer.`,
    );
  }
  return displayName;
}

export function isUserProfile(value: unknown): value is UserProfile {
  if (!value || typeof value !== 'object') return false;
  const profile = value as Partial<UserProfile>;
  return (
    typeof profile.id === 'string' &&
    profile.id.length > 0 &&
    typeof profile.displayName === 'string' &&
    profile.displayName.trim().length > 0 &&
    profile.displayName.length <= MAX_DISPLAY_NAME_LENGTH &&
    profile.profileVersion === USER_PROFILE_VERSION &&
    typeof profile.createdAt === 'string' &&
    typeof profile.updatedAt === 'string'
  );
}

export class UserProfileRepository {
  constructor(
    private readonly storage: ProfileStorage,
    private readonly now: () => Date = () => new Date(),
    private readonly createId: () => string = createClientId,
  ) {}

  load(): UserProfile | null {
    try {
      const raw = this.storage.getItem(USER_PROFILE_STORAGE_KEY);
      if (raw === null) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!isUserProfile(parsed)) {
        throw new UserProfileError('The local profile could not be read.');
      }
      return parsed;
    } catch (error) {
      if (error instanceof UserProfileError) throw error;
      throw new UserProfileError('The local profile could not be read.');
    }
  }

  create(displayNameInput: string): UserProfile {
    const existing = this.load();
    if (existing) return existing;
    const timestamp = this.now().toISOString();
    const profile: UserProfile = {
      id: this.createId(),
      displayName: normalizeDisplayName(displayNameInput),
      profileVersion: USER_PROFILE_VERSION,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    this.persist(profile);
    return profile;
  }

  update(profile: UserProfile, displayNameInput: string): UserProfile {
    const updated: UserProfile = {
      ...profile,
      displayName: normalizeDisplayName(displayNameInput),
      updatedAt: this.now().toISOString(),
    };
    this.persist(updated);
    return updated;
  }

  private persist(profile: UserProfile): void {
    try {
      this.storage.setItem(USER_PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch {
      throw new UserProfileError(
        'Your profile could not be saved on this device. Please try again.',
      );
    }
  }
}

export function createBrowserUserProfileRepository(): UserProfileRepository {
  const browserStorage: ProfileStorage = {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
    removeItem: (key) => window.localStorage.removeItem(key),
  };
  return new UserProfileRepository(browserStorage);
}
